import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { toast } from "react-toastify";
import { FiMail, FiMapPin, FiSend } from "react-icons/fi";
import { contactAPI } from "../services/api";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactRef, contactInView] = useInView({ threshold: 0.1 });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await contactAPI.sendMessage(formData);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-gray-50 dark:bg-gray-900 transition-colors">
      <section ref={contactRef} className="section-padding">
        <div className="max-w-7xl mx-auto px-4 lg:px-0">
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={contactInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold mb-4 text-gray-900 dark:text-white">
              Get In Touch
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Have a project in mind or want to collaborate? Send me a message
              and I’ll respond promptly.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={contactInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="backdrop-blur-md bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-3xl p-10 shadow-2xl"
            >
              <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
                Send a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Floating Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FloatingInput
                    label="Your Name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  <FloatingInput
                    label="Your Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <FloatingInput
                  label="Subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                />
                <FloatingTextarea
                  label="Your Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-primary-600 to-blue-600 hover:from-blue-600 hover:to-primary-600 text-white font-semibold rounded-2xl shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <FiSend /> Send Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={contactInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="space-y-8"
            >
              <InfoCard
                icon={<FiMail />}
                title="Email"
                description="infoshihab00000@gmail.com"
              />
              {/* <InfoCard
                icon={<FiPhone />}
                title="Phone"
                description="+1 (555) 123-4567"
              /> */}
              <InfoCard
                icon={<FiMapPin />}
                title="Location"
                description="Dhaka, Bangladesh"
              />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={contactInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="p-6 rounded-2xl bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/10 dark:to-blue-900/10 shadow-lg"
              >
                <h3 className="font-semibold mb-2 text-primary-800 dark:text-primary-200">
                  Quick Response
                </h3>
                <p className="text-primary-700 dark:text-primary-300 text-sm">
                  I usually respond within 24 hours. For urgent inquiries, call
                  or message directly.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={contactInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg space-y-3"
              >
                <h3 className="font-semibold text-lg">Current Availability</h3>
                <AvailabilityItem label="New Projects" status="Available" />
                <AvailabilityItem label="Consultations" status="Available" />
                <AvailabilityItem label="Collaborations" status="Open" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

const FloatingInput = ({ label, name, type, value, onChange }) => (
  <div className="relative">
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required
      placeholder=" "
      className="w-full px-4 pt-5 pb-3 border rounded-2xl bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition peer"
    />
    <label className="absolute left-4 top-3 text-gray-500 dark:text-gray-400 text-sm transition-all peer-placeholder-shown:top-5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-base peer-focus:top-1.5 peer-focus:text-sm peer-focus:text-primary-600 dark:peer-focus:text-primary-400">
      {label} *
    </label>
  </div>
);

const FloatingTextarea = ({ label, name, value, onChange }) => (
  <div className="relative">
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows={6}
      required
      placeholder=" "
      className="w-full px-4 pt-5 pb-3 border rounded-2xl bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition peer resize-none"
    />
    <label className="absolute left-4 top-3 text-gray-500 dark:text-gray-400 text-sm transition-all peer-placeholder-shown:top-5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-base peer-focus:top-1.5 peer-focus:text-sm peer-focus:text-primary-600 dark:peer-focus:text-primary-400">
      {label} *
    </label>
  </div>
);

const InfoCard = ({ icon, title, description }) => (
  <motion.div
    whileHover={{ scale: 1.03 }}
    className="flex items-start space-x-4 bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-lg transition"
  >
    <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-xl text-primary-600 text-2xl">
      {icon}
    </div>
    <div>
      <h4 className="font-semibold mb-1 text-gray-900 dark:text-white">
        {title}
      </h4>
      <p className="text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  </motion.div>
);

const AvailabilityItem = ({ label, status }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-600 dark:text-gray-400">{label}</span>
    <span
      className={`px-3 py-1 rounded-full text-sm font-medium transition ${
        status === "Open" || status === "Available"
          ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
          : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
      }`}
    >
      {status}
    </span>
  </div>
);

export default Contact;
